import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Search, Download, Plus, Loader2 } from "lucide-react";
import { parseDateOnly } from "@/lib/dateUtils";

interface AuthorityLettersSearchModalProps {
  open: boolean;
  onClose: () => void;
  workOrderId: number;
}

export function AuthorityLettersSearchModal({ open, onClose, workOrderId }: AuthorityLettersSearchModalProps) {
  const { toast } = useToast();
  const [mcNumber, setMcNumber] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const searchMutation = useMutation({
    mutationFn: async (mc: string) => {
      const response = await apiRequest("GET", `/api/helix/docs/docket?docketNumber=MC-${mc}`) as { documents?: any[] };
      return response;
    },
    onSuccess: (data) => {
      const results = data?.documents || [];
      setSearchResults(results);
      if (results.length === 0) {
        toast({
          title: "No Results",
          description: "No authority letters found for this MC number.",
        });
      }
    },
    onError: () => {
      toast({
        title: "Search Failed",
        description: "Failed to search for authority letters. Please try again.",
        variant: "destructive",
      });
    },
  });

  const saveLetterMutation = useMutation({
    mutationFn: async (document: any) => {
      const letterData = {
        work_order_id: workOrderId,
        helix_document_id: document.document_identifier,
        document_identifier: document.document_identifier,
        docket_full: document.docket_full,
        company_name: document.company_name,
        letter_type: document.letter_type,
        service_date: document.service_date,
        download_url: document.download_url,
      };
      await apiRequest("POST", "/api/authority-letters", letterData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/authority-letters`] });
      toast({
        title: "Success",
        description: "Authority letter saved to work order",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save authority letter",
        variant: "destructive",
      });
    },
  });

  const handleSearch = () => {
    if (!mcNumber.trim()) {
      toast({
        title: "Invalid Input",
        description: "Please enter an MC number",
        variant: "destructive",
      });
      return;
    }
    setIsSearching(true);
    searchMutation.mutate(mcNumber, {
      onSettled: () => setIsSearching(false),
    });
  };

  const handleSaveLetter = (document: any) => {
    saveLetterMutation.mutate(document);
  };

  const handleDownload = (downloadUrl: string) => {
    window.open(downloadUrl, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Search Authority Letters</DialogTitle>
          <DialogDescription>
            Search for authority letters by MC number from the FMCSA database
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="mc-number">Docket Number</Label>
              <Input
                id="mc-number"
                placeholder="Enter MC number (e.g., 123456)"
                value={mcNumber}
                onChange={(e) => setMcNumber(e.target.value.replace(/\D/g, ''))}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="input-mc-number"
              />
            </div>
            <div className="flex items-end">
              <Button 
                onClick={handleSearch} 
                disabled={isSearching || !mcNumber.trim()}
                data-testid="button-search-letters"
              >
                {isSearching ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
            </div>
          </div>

          {searchResults.length > 0 && (
            <div className="border rounded-lg">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Document ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Company Name</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Letter Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Service Date</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {searchResults.map((doc, index) => (
                      <tr 
                        key={doc.document_identifier || index} 
                        className="hover:bg-gray-50"
                        data-testid={`search-result-${index}`}
                      >
                        <td className="px-4 py-3 text-sm">{doc.document_identifier}</td>
                        <td className="px-4 py-3 text-sm">{doc.company_name}</td>
                        <td className="px-4 py-3 text-sm">{doc.letter_type || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm">
                          {doc.service_date ? parseDateOnly(doc.service_date).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(doc.download_url)}
                              data-testid={`button-download-${index}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSaveLetter(doc)}
                              disabled={saveLetterMutation.isPending}
                              data-testid={`button-save-${index}`}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Save
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {searchResults.length === 0 && searchMutation.isSuccess && (
            <div className="text-center py-8 text-gray-500">
              <p>No authority letters found for MC-{mcNumber}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
